import React, { useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'motion/react';
import {
  CheckCircle2, Clock, Copy, Send, Shield,
  MessageSquare, Trash2, ChevronDown, ChevronUp, Bell
} from 'lucide-react';
import { LoyaltyTest } from '../types';
import AdminAnalytics from './AdminAnalytics';
import AdminReports from './AdminReports';
import { excludeOperatorByDefault } from '../lib/analytics';
import { getDispatchMessage, isFollowUpDispatch } from '../lib/dispatchMessages';
import { ownerFirstName } from '../lib/ownerName';
import OpenChatButton from '../components/OpenChatButton';
import { enablePushForCreator, relinkPushIfGranted, isPushCapable, hasPushPermission, isIosNotInstalled } from '../lib/push';

interface AdminProps {
  user: User;
}

const ADMIN_EMAILS = ['voidanon16@proton.me'];

// A dispatched session with no answer after this long lands in the Nudges tab.
const NUDGE_AFTER_MS = 60 * 60 * 1000;
// Sessions that went cold for days aren't worth chasing.
const NUDGE_GIVE_UP_MS = 72 * 60 * 60 * 1000;

function formatWaiting(ms: number): string {
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  return hrs < 24 ? `${hrs}h ${mins % 60}m` : `${Math.floor(hrs / 24)}d ${hrs % 24}h`;
}

// wa.me needs digits only, international. Local Ghana numbers (0XX…) get 233.
function whatsappDigits(phone: string): string {
  const d = phone.replace(/\D/g, '');
  return d.startsWith('0') ? `233${d.slice(1)}` : d;
}

const PLATFORM_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  WhatsApp: { bg: 'bg-[#25D366]/10', text: 'text-[#25D366]', label: 'WhatsApp' },
  Snapchat: { bg: 'bg-[#FFFC00]/10', text: 'text-[#FFFC00]', label: 'Snapchat' },
  Telegram: { bg: 'bg-[#0088cc]/10', text: 'text-[#0088cc]', label: 'Telegram' },
  Instagram: { bg: 'bg-[#E1306C]/10', text: 'text-[#E1306C]', label: 'Instagram' },
  TikTok: { bg: 'bg-[#25F4EE]/10', text: 'text-[#25F4EE]', label: 'TikTok' },
};

export default function Admin({ user }: AdminProps) {
  const [tests, setTests] = useState<LoyaltyTest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'dispatched' | 'all'>('pending');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [dispatching, setDispatching] = useState<string | null>(null);
  const [reminders, setReminders] = useState<any[]>([]);
  const [sendingReminder, setSendingReminder] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'inquiries' | 'reminders' | 'retakes' | 'analytics' | 'reports' | 'nudges'>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return t === 'reports' ? 'reports' : t === 'nudges' ? 'nudges' : 'inquiries';
  });
  // Sessions the sender has already asked a reminder for — no nudge needed.
  const [remindedTestIds, setRemindedTestIds] = useState<Set<string>>(new Set());
  const [nudging, setNudging] = useState<string | null>(null);
  const [retakeNotifications, setRetakeNotifications] = useState<any[]>([]);
  const [sendingRetake, setSendingRetake] = useState<string | null>(null);
  // creator_id -> phone_number, so we can text/WhatsApp the sender once
  // their session gets answered.
  const [creatorPhones, setCreatorPhones] = useState<Record<string, string>>({});
  // email (lowercased) -> phone, a fallback for sessions with no/mismatched creator_id
  const [phonesByEmail, setPhonesByEmail] = useState<Record<string, string>>({});
  // Why the sender-phone lookup failed, shown as a banner so it isn't silent.
  const [phoneIssue, setPhoneIssue] = useState('');

  const [alertsOn, setAlertsOn] = useState(false);
  const [alertsLoading, setAlertsLoading] = useState(false);

  const isAdmin = ADMIN_EMAILS.includes(user.email || '');
  const [newReports, setNewReports] = useState(0);

  useEffect(() => { if (isAdmin) { fetchAll(); fetchReminders(); fetchRetakeNotifications(); fetchRemindedIds(); } }, []);
  // Keep the operator's own taps out of the analytics numbers.
  useEffect(() => { if (isAdmin) excludeOperatorByDefault(); }, [isAdmin]);
  // Badge on the Reports tab (stays 0 until supabase/reports.sql has been run).
  useEffect(() => {
    if (!isAdmin) return;
    supabase.rpc('admin_list_reports', { p_status: 'new', p_limit: 100 }).then(({ data, error }) => {
      if (!error && Array.isArray(data)) setNewReports(data.length);
    });
  }, [isAdmin]);
  // Dispatch alerts: quietly re-link this device if permission was already
  // granted (never prompts). Push subscriptions are per device/browser, so
  // each device the operator wants alerts on has to be enabled once.
  useEffect(() => {
    if (!isAdmin) return;
    setAlertsOn(hasPushPermission());
    relinkPushIfGranted(user.id).then(() => setAlertsOn(hasPushPermission()));
  }, [isAdmin, user.id]);

  const handleEnableAlerts = async () => {
    if (alertsLoading) return;
    setAlertsLoading(true);
    try {
      const result = await enablePushForCreator(user.id);
      if (result === 'granted') {
        setAlertsOn(true);
        showToast('Dispatch alerts on 🔔');
      } else if (result === 'denied') {
        showToast('Notifications are blocked — allow them in your browser settings.');
      } else if (result.startsWith('error:')) {
        showToast(`Alert setup failed: ${result.slice(6)}`);
      } else {
        showToast("Push isn't supported on this device/browser.");
      }
    } finally {
      setAlertsLoading(false);
    }
  };

  const fetchAll = async () => {
    try {
      const { data, error } = await supabase
        .from('tests')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setTests(data || []);
      await fetchCreatorPhones(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Sender phones live on private profile rows, so the browser can only read
  // the operator's own. Ask the admin-sender-phones function (service role,
  // admin-only) instead; fall back to a direct read if it isn't deployed yet.
  const fetchCreatorPhones = async (allTests: LoyaltyTest[]) => {
    const ids = [...new Set(allTests.map(t => t.creator_id).filter(Boolean) as string[])];
    const emails = [...new Set(allTests.map(t => t.creator_email?.toLowerCase()).filter(Boolean) as string[])];

    try {
      const { data, error } = await supabase.functions.invoke('admin-sender-phones', { body: { ids, emails } });
      if (error) throw error;
      setCreatorPhones(data?.byId || {});
      setPhonesByEmail(data?.byEmail || {});
      setPhoneIssue('');
      return;
    } catch (err) {
      console.error('admin-sender-phones failed, falling back to direct read:', err);
      const ctx = (err as { context?: Response })?.context;
      const status = ctx?.status;
      let detail = '';
      try { detail = ctx ? (await ctx.clone().text()).slice(0, 200) : ''; } catch { /* ignore */ }
      setPhoneIssue(
        status === 403
          ? `Your account isn't in the ADMIN_USER_IDS secret, so sender phones are hidden. Add this id to it: ${user.id}`
          : status === 404
          ? 'Phone lookup function is not deployed yet.'
          : `Phone lookup failed${status ? ` (${status})` : ''}: ${detail || (err as Error)?.message || 'unknown error'}`
      );
    }

    const byId: Record<string, string> = {};
    const byEmail: Record<string, string> = {};
    for (let i = 0; i < ids.length; i += 60) {
      const { data, error } = await supabase.from('profiles').select('id, email, phone_number').in('id', ids.slice(i, i + 60));
      if (error) { console.error('phone lookup failed:', error); continue; }
      (data || []).forEach((p: { id: string; email: string | null; phone_number: string | null }) => {
        if (!p.phone_number) return;
        byId[p.id] = p.phone_number;
        if (p.email) byEmail[p.email.toLowerCase()] = p.phone_number;
      });
    }
    setCreatorPhones(byId);
    setPhonesByEmail(byEmail);
  };

  const phoneFor = (t: LoyaltyTest): string | undefined =>
    (t.creator_id && creatorPhones[t.creator_id]) ||
    (t.creator_email ? phonesByEmail[t.creator_email.toLowerCase()] : undefined) ||
    undefined;

  const fetchReminders = async () => {
    const { data: reminderData } = await supabase
      .from('reminders')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (!reminderData?.length) { setReminders([]); return; }

    const testIds = [...new Set(reminderData.map(r => r.test_id))];
    const { data: testsData } = await supabase.from('tests').select('*').in('id', testIds);
    const testsMap = Object.fromEntries((testsData || []).map(t => [t.id, t]));

    setReminders(reminderData.map(r => ({ ...r, tests: testsMap[r.test_id] || null })));
  };

  const fetchRemindedIds = async () => {
    const { data } = await supabase.from('reminders').select('test_id');
    setRemindedTestIds(new Set((data || []).map((r: { test_id: string }) => r.test_id)));
  };

  const markNudged = async (test: LoyaltyTest) => {
    if (!test.id) return;
    setNudging(test.id);
    try {
      const now = new Date().toISOString();
      const { error } = await supabase.from('tests').update({ creator_nudged_at: now }).eq('id', test.id);
      if (error) throw error;
      setTests(prev => prev.map(t => (t.id === test.id ? { ...t, creator_nudged_at: now } : t)));
      showToast(`✅ Nudge logged for ${test.partner_name}`);
    } catch (err) {
      console.error(err);
      showToast('Error saving nudge');
    } finally {
      setNudging(null);
    }
  };

  const fetchRetakeNotifications = async () => {
    const { data } = await supabase
      .from('retake_notifications')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });
    setRetakeNotifications(data || []);
  };

  const markRetakeSent = async (id: string) => {
    setSendingRetake(id);
    try {
      await supabase.from('retake_notifications').update({ status: 'sent', sent_at: new Date().toISOString() }).eq('id', id);
      setRetakeNotifications(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      console.error(err);
    } finally {
      setSendingRetake(null);
    }
  };

  const markDispatched = async (test: LoyaltyTest) => {
    if (!test.id) return;
    setDispatching(test.id);
    try {
      const { error } = await supabase
        .from('tests')
        .update({ status: 'dispatched', updated_at: new Date().toISOString() })
        .eq('id', test.id);
      if (error) throw error;
      await fetchAll();
      setFilter('dispatched'); // Switch to dispatched tab so user sees it moved
      showToast(`✅ Dispatched to ${test.partner_name}`);
    } catch (err) {
      console.error(err);
      showToast('Error updating status');
    } finally {
      setDispatching(null);
    }
  };

  const sendReminder = async (reminder: any) => {
    setSendingReminder(reminder.id);
    try {
      const test = reminder.tests;
      const testUrl = `${window.location.origin}/test/${test.id}`;
      const message = `Hey ${test.partner_name} 👀\nJust a quick reminder — someone close to you is still waiting on your honest answers.\n\nIt only takes a few minutes:\nhttps://yhuu.life/test/${test.id}\n\n— Yhuu`;

      await supabase.functions.invoke('send-message', {
        body: { to: test.partner_contact, message, channel: test.partner_platform },
      });

      await supabase.from('reminders').update({ status: 'sent' }).eq('id', reminder.id);
      setReminders(reminders.filter(r => r.id !== reminder.id));
      showToast(`Reminder sent to ${test.partner_name}!`);
    } catch (error) {
      showToast('Failed to send reminder.');
    } finally {
      setSendingReminder(null);
    }
  };

  const copyMessage = (test: LoyaltyTest) => {
    const msg = getDispatchMessage(test);
    navigator.clipboard.writeText(msg);
    showToast('Message copied!');
  };

  const copyContact = (contact: string) => {
    navigator.clipboard.writeText(contact);
    showToast('Contact copied!');
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const filtered = tests.filter(t =>
    filter === 'all' ? true : t.status === filter
  );

  const pendingCount = tests.filter(t => t.status === 'pending').length;

  // Sent to the recipient over an hour ago, still unanswered, sender not yet
  // nudged and hasn't requested a reminder themselves. Oldest wait first.
  const nudgeQueue = tests
    .filter(t => {
      if (t.status !== 'dispatched' || !t.dispatched_at || t.creator_nudged_at) return false;
      if (t.id && remindedTestIds.has(t.id)) return false;
      const age = Date.now() - new Date(t.dispatched_at).getTime();
      return age >= NUDGE_AFTER_MS && age <= NUDGE_GIVE_UP_MS;
    })
    .sort((a, b) => new Date(a.dispatched_at!).getTime() - new Date(b.dispatched_at!).getTime());

  const buildNudgeMessage = (test: LoyaltyTest) => {
    const name = ownerFirstName(test.creator_email) || 'there';
    return `Hey ${name} 👋🏽\n${test.partner_name} hasn't answered your Yhuu yet. Open Yhuu and send them a reminder — you can also check if they've opened it 👀\n\nhttps://yhuu.life/dashboard\n\n— Yhuu`;
  };

  if (!isAdmin) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <Shield className="w-12 h-12 text-zinc-700 mb-4" />
        <h2 className="text-xl font-light text-white mb-2">Access Restricted</h2>
        <p className="text-zinc-500 text-sm">This area is for Yhuu operators only.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-6 px-4">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-1">
          <Shield className="w-4 h-4 text-brand-accent" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Operator Panel</span>
        </div>
        <h1 className="text-3xl font-light text-white leading-tight">
          Dispatch <span className="italic font-serif text-brand-accent">Control</span>
        </h1>
        <p className="text-zinc-500 mt-1 text-sm font-light">
          Send summons from official Yhuu accounts.
        </p>
      </div>

      {/* Dispatch alerts — push the moment a session needs sending by hand */}
      {!alertsOn && isPushCapable() && (
        <div className="flex items-center justify-between gap-3 flex-wrap bg-brand-card border border-brand-border rounded-2xl px-4 py-3 mb-6">
          <div className="flex items-center gap-2.5">
            <Bell className="w-4 h-4 text-brand-accent shrink-0" />
            <span className="text-xs text-zinc-400 font-medium">
              Get a push the moment a session needs dispatching.
            </span>
          </div>
          <button
            onClick={handleEnableAlerts}
            disabled={alertsLoading}
            className="px-4 py-2 rounded-xl bg-brand-accent text-white text-[10px] font-bold uppercase tracking-widest cursor-pointer disabled:opacity-60 whitespace-nowrap"
          >
            {alertsLoading ? 'Enabling...' : 'Enable alerts'}
          </button>
        </div>
      )}
      {!alertsOn && !isPushCapable() && isIosNotInstalled() && (
        <div className="flex items-center gap-2.5 bg-brand-card border border-brand-border rounded-2xl px-4 py-3 mb-6">
          <Bell className="w-4 h-4 text-brand-accent shrink-0" />
          <span className="text-xs text-zinc-400 font-medium">
            To get dispatch alerts on iPhone: tap Share → "Add to Home Screen", then open Yhuu from there.
          </span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Pending', value: tests.filter(t => t.status === 'pending').length, color: 'text-brand-accent' },
          { label: 'Dispatched', value: tests.filter(t => t.status === 'dispatched').length, color: 'text-blue-400' },
          { label: 'Completed', value: tests.filter(t => t.status === 'completed').length, color: 'text-emerald-400' },
        ].map(stat => (
          <div key={stat.label} className="bg-brand-card border border-brand-border rounded-2xl p-4 text-center">
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
            <div className="text-[10px] text-zinc-600 uppercase tracking-widest mt-1">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Main Tabs */}
      <div className="flex gap-2 mb-6">
        {(['inquiries', 'nudges', 'reminders', 'retakes', 'reports', 'analytics'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all cursor-pointer ${
              activeTab === tab
                ? 'bg-brand-accent text-white'
                : 'bg-brand-card border border-brand-border text-zinc-500 hover:text-white'
            }`}
          >
            {tab === 'nudges' && nudgeQueue.length > 0 ? `Nudges (${nudgeQueue.length})` : tab === 'reminders' && reminders.length > 0 ? `Reminders (${reminders.length})` : tab === 'retakes' && retakeNotifications.length > 0 ? `Retakes (${retakeNotifications.length})` : tab === 'reports' && newReports > 0 ? `Reports (${newReports})` : tab}
          </button>
        ))}
      </div>

      {activeTab === 'inquiries' ? (
        <>
          {/* Filter Tabs */}
          <div className="flex gap-2 mb-6">
            {(['pending', 'dispatched', 'all'] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all cursor-pointer ${
                  filter === f
                    ? 'bg-brand-accent text-white'
                    : 'bg-brand-card border border-brand-border text-zinc-500 hover:text-white'
                }`}
              >
                {f === 'pending' && pendingCount > 0 ? `${f} (${pendingCount})` : f}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <div key={i} className="h-24 bg-brand-card rounded-2xl animate-pulse border border-brand-border" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-zinc-600 text-sm">
              No {filter === 'all' ? '' : filter} inquiries.
            </div>
          ) : (
            <div className="space-y-3">
              <AnimatePresence initial={false}>
                {filtered.map(test => {
                  const platform = PLATFORM_STYLES[test.partner_platform || ''] || PLATFORM_STYLES['WhatsApp'];
                  const isExpanded = expandedId === test.id;
                  const isDispatching = dispatching === test.id;

                  return (
                    <motion.div
                      key={test.id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="bg-brand-card border border-brand-border rounded-2xl overflow-hidden"
                    >
                      <div className="p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${
                                test.status === 'completed' ? 'bg-emerald-500/10 text-emerald-500' :
                                test.status === 'dispatched' ? 'bg-blue-500/10 text-blue-400' :
                                'bg-brand-accent/10 text-brand-accent'
                              }`}>
                                {test.status === 'dispatched' ? 'Sent ✅' : test.status}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest ${platform.bg} ${platform.text}`}>
                                {test.partner_platform}
                              </span>
                              {/* WhatsApp sessions only land in the queue when the auto-send failed for good */}
                              {test.status === 'pending' && test.partner_platform === 'WhatsApp' && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-amber-500/10 text-amber-400">
                                  ⚠️ Auto-send failed
                                </span>
                              )}
                              {/* Recipient opted into a follow-up, but the sender hasn't
                                  used their one shot yet — the fallback for senders who
                                  don't have push (not on their Home Screen). Ping them
                                  manually via the phone number below. */}
                              {test.follow_up_enabled && !test.follow_up_used && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-pink-500/10 text-pink-400">
                                  👀 Follow-up ready
                                </span>
                              )}
                              {/* Sender already sent their one follow-up question — this
                                  session is back in the queue for a fresh manual dispatch,
                                  same as a brand-new session, just with a different message
                                  (see "Message to Send (Follow-up)" below). */}
                              {isFollowUpDispatch(test) && (
                                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-violet-500/10 text-violet-400">
                                  📨 Follow-up to dispatch
                                </span>
                              )}
                              <span className="text-[10px] text-zinc-600 ml-auto">
                                {new Date(test.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              </span>
                            </div>

                            <h3 className="text-lg font-light text-white">{test.partner_name}</h3>

                            <button
                              onClick={() => copyContact(test.partner_contact)}
                              className="text-xs text-zinc-500 hover:text-white transition-colors mt-0.5 flex items-center gap-1 cursor-pointer"
                            >
                              <Copy className="w-3 h-3" />
                              {test.partner_contact}
                            </button>

                            <div className="mt-2 pt-2 border-t border-brand-border/40 text-[11px] text-zinc-600 flex flex-col gap-0.5">
                              <span>Sender: {test.creator_email || 'unknown'}</span>
                              <button
                                onClick={() => phoneFor(test) && copyContact(phoneFor(test)!)}
                                className={`flex items-center gap-1 w-fit ${phoneFor(test) ? 'text-emerald-500 hover:text-emerald-400 cursor-pointer' : 'text-zinc-700'}`}
                              >
                                📞 {phoneFor(test) || 'no phone on file'}
                              </button>
                            </div>
                          </div>

                          <button
                            onClick={() => setExpandedId(isExpanded ? null : (test.id || null))}
                            className="p-2 text-zinc-600 hover:text-white transition-colors cursor-pointer"
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </button>
                        </div>

                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="mt-4 p-4 bg-zinc-950/60 rounded-xl border border-brand-border/50">
                                <p className="text-[10px] text-zinc-600 font-bold uppercase tracking-widest mb-2">
                                  {isFollowUpDispatch(test) ? 'Message to Send (Follow-up)' : 'Message to Send'}
                                </p>
                                <p className="text-xs text-zinc-300 leading-relaxed font-serif italic whitespace-pre-line">
                                  {getDispatchMessage(test)}
                                </p>
                              </div>

                              <div className="mt-3 space-y-2">
                                <p className="text-[10px] text-zinc-600 font-bold uppercase tracking-widest flex items-center gap-1.5">
                                  <MessageSquare className="w-3 h-3" />
                                  {test.questions.length} Questions
                                </p>
                                {test.questions.map((q, i) => (
                                  <div key={q.id} className="text-xs text-zinc-400 pl-3 border-l border-brand-border">
                                    <span className="text-zinc-600">{i + 1}. </span>{q.text}
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {test.status === 'pending' && (
                        <OpenChatButton
                          platform={test.partner_platform}
                          contact={test.partner_contact}
                          message={getDispatchMessage(test)}
                          onNotify={showToast}
                          className="px-5 pb-3"
                        />
                      )}

                      {test.status === 'pending' && (
                        <div className="px-5 pb-5 flex gap-2">
                          <button
                            onClick={() => copyMessage(test)}
                            className="flex-1 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 uppercase tracking-widest cursor-pointer"
                          >
                            <Copy className="w-3.5 h-3.5" />
                            Copy Message
                          </button>
                          <button
                            onClick={() => markDispatched(test)}
                            disabled={isDispatching}
                            className="flex-1 py-2.5 bg-brand-accent hover:bg-rose-500 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 uppercase tracking-widest cursor-pointer disabled:opacity-50"
                          >
                            {isDispatching ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <>
                                <Send className="w-3.5 h-3.5" />
                                Mark Sent
                              </>
                            )}
                          </button>
                        </div>
                      )}

                      {test.status === 'dispatched' && (
                        <div className="px-5 pb-5">
                          <div className="py-2.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-xl text-xs font-bold flex items-center justify-center gap-2 uppercase tracking-widest">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Summons Sent
                          </div>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </>
      ) : activeTab === 'nudges' ? (
        <div className="space-y-3">
          {phoneIssue && (
            <div className="text-[11px] leading-relaxed text-amber-500 bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 break-all">
              ⚠️ {phoneIssue}
            </div>
          )}
          <p className="text-zinc-500 text-xs font-light">
            Sent an hour or more ago and still unanswered. Message the sender to come back and remind their recipient.
          </p>
          {nudgeQueue.length === 0 ? (
            <div className="text-center py-16 text-zinc-600 text-sm">No senders to nudge right now.</div>
          ) : nudgeQueue.map(test => {
            const phone = phoneFor(test);
            const message = buildNudgeMessage(test);
            const waHref = phone ? `https://wa.me/${whatsappDigits(phone)}?text=${encodeURIComponent(message)}` : null;
            const waiting = formatWaiting(Date.now() - new Date(test.dispatched_at!).getTime());
            return (
              <div key={test.id} className="bg-brand-card border border-brand-border rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-amber-500/10 text-amber-400">
                    ⏰ Waiting {waiting}
                  </span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-zinc-800 text-zinc-400">
                    {test.partner_platform}
                  </span>
                  {test.opened_at && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-blue-500/10 text-blue-400">
                      Opened, not answered
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-light text-white">{test.partner_name}</h3>
                <div className="mt-2 pt-2 border-t border-brand-border/40 text-[11px] text-zinc-600 flex flex-col gap-0.5">
                  <span>Sender: {test.creator_email || 'unknown'}</span>
                  <button
                    onClick={() => phone && copyContact(phone)}
                    className={`flex items-center gap-1 w-fit ${phone ? 'text-emerald-500 hover:text-emerald-400 cursor-pointer' : 'text-zinc-700'}`}
                  >
                    📞 {phone || 'no phone on file'}
                  </button>
                </div>
                <p className="mt-3 text-xs text-zinc-300 leading-relaxed font-serif italic whitespace-pre-line bg-zinc-950/60 rounded-xl border border-brand-border/50 p-3">
                  {message}
                </p>
                {waHref && (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 block w-full text-center py-2.5 rounded-xl text-xs font-bold uppercase tracking-widest bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/30"
                  >
                    Open WhatsApp with message
                  </a>
                )}
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => { navigator.clipboard.writeText(message); showToast('Message copied!'); }}
                    className="flex-1 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 uppercase tracking-widest cursor-pointer"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy
                  </button>
                  <button
                    onClick={() => markNudged(test)}
                    disabled={nudging === test.id}
                    className="flex-1 py-2.5 bg-brand-accent hover:bg-rose-500 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 uppercase tracking-widest cursor-pointer disabled:opacity-50"
                  >
                    {nudging === test.id ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <><Send className="w-3.5 h-3.5" /> Mark Nudged</>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : activeTab === 'reminders' ? (
        <div className="space-y-3">
          {reminders.length === 0 ? (
            <div className="text-center py-16 text-zinc-600 text-sm">No pending reminders.</div>
          ) : reminders.filter(r => r.tests).map(reminder => (
            <div key={reminder.id} className="bg-brand-card border border-brand-border rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <Bell className="w-4 h-4 text-brand-accent" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Reminder Request</span>
              </div>
              <h3 className="text-white font-light text-lg mb-1">{reminder.tests.partner_name}</h3>
              <p className="text-zinc-500 text-xs mb-4">{reminder.tests.partner_contact}</p>
              <OpenChatButton
                platform={reminder.tests.partner_platform}
                contact={reminder.tests.partner_contact}
                message={`Hey ${reminder.tests.partner_name} 👀\nJust a quick reminder — someone close to you is still waiting on your honest answers.\n\nIt only takes a few minutes:\nhttps://yhuu.life/test/${reminder.tests.id}\n\n— Yhuu`}
                onNotify={showToast}
                className="mb-3"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const testUrl = `${window.location.origin}/test/${reminder.tests.id}`;
                    const msg = `Hey ${reminder.tests.partner_name} 👀\nJust a quick reminder — someone close to you is still waiting on your honest answers.\n\nIt only takes a few minutes:\nhttps://yhuu.life/test/${reminder.tests.id}\n\n— Yhuu`;
                    navigator.clipboard.writeText(msg);
                    showToast('Reminder message copied!');
                  }}
                  className="flex-1 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 uppercase tracking-widest cursor-pointer"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy Message
                </button>
                <button
                  onClick={() => sendReminder(reminder)}
                  disabled={sendingReminder === reminder.id}
                  className="flex-1 py-2.5 bg-brand-accent hover:bg-rose-500 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 uppercase tracking-widest cursor-pointer disabled:opacity-50"
                >
                  {sendingReminder === reminder.id ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Send className="w-3.5 h-3.5" /> Send Reminder</>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Reports Tab */}
      {activeTab === 'reports' && <AdminReports onNewCount={setNewReports} />}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && <AdminAnalytics />}

      {/* Retakes Tab */}
      {activeTab === 'retakes' && (
        <div className="space-y-3">
          {retakeNotifications.length === 0 ? (
            <div className="text-center py-16 text-zinc-600 text-sm">No pending retake notifications.</div>
          ) : retakeNotifications.map(r => (
            <div key={r.id} className="bg-brand-card border border-brand-border rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-brand-accent">Retake Notification</span>
              </div>
              <p className="text-zinc-400 text-xs mb-1">{r.platform} · {r.recipient}</p>
              <p className="text-zinc-300 text-sm mb-4 whitespace-pre-line bg-zinc-900 rounded-xl p-3">{r.message}</p>
              <div className="flex gap-2">
                <button
                  onClick={() => { navigator.clipboard.writeText(r.message); showToast('Message copied!'); }}
                  className="flex-1 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 uppercase tracking-widest cursor-pointer"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
                <button
                  onClick={() => markRetakeSent(r.id)}
                  disabled={sendingRetake === r.id}
                  className="flex-1 py-2.5 bg-brand-accent hover:bg-rose-500 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 uppercase tracking-widest cursor-pointer disabled:opacity-50"
                >
                  {sendingRetake === r.id ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><Send className="w-3.5 h-3.5" /> Mark Sent</>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-8 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 bg-white text-zinc-950 px-6 py-4 rounded-2xl font-bold shadow-2xl z-50 flex items-center gap-3 whitespace-nowrap"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            <span className="text-sm">{toast}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
